import { Suspense, lazy, useEffect } from "react";
import { useDispatch } from "react-redux";
import ScrollToTop from "./helpers/scroll-top";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { CartDrawerProvider } from "./contexts/CartDrawerContext";
import CartDrawer from "./components/cart-drawer/CartDrawer";
import ProtectedRoute from "./components/ProtectedRoute";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { loadUserFromStorage } from "./store/slices/user-slice";
import { fetchCart } from "./store/slices/cart-slice";
import { fetchWishlist } from "./store/slices/wishlist-slice";
import { fetchGeneralSettings } from "./store/slices/settings-slice";

// home pages
const HomeFashionThree = lazy(() => import("./pages/home/HomeFashionThree"));
// shop pages
const ShopGridNoSidebar = lazy(() => import("./pages/shop/ShopGridNoSidebar"));
// product page
const Product = lazy(() => import("./pages/shop-product/Product"));
// blog pages
const BlogNoSidebar = lazy(() => import("./pages/blog/BlogNoSidebar"));
const BlogDetailsStandard = lazy(() =>
  import("./pages/blog/BlogDetailsStandard")
);
// other pages
const About = lazy(() => import("./pages/other/About"));
const Contact = lazy(() => import("./pages/other/Contact"));
const MyAccount = lazy(() => import("./pages/other/MyAccount"));
const LoginRegister = lazy(() => import("./pages/other/LoginRegister"));
const Cart = lazy(() => import("./pages/other/Cart"));
const Wishlist = lazy(() => import("./pages/other/Wishlist"));
const Compare = lazy(() => import("./pages/other/Compare"));
const Checkout = lazy(() => import("./pages/other/Checkout"));
const Success = lazy(() => import("./pages/other/Success"));
const PaymentFailed = lazy(() => import("./pages/other/PaymentFailed"));
const OrderHistory = lazy(() => import("./pages/other/OrderHistory"));
const OrderDetails = lazy(() => import("./pages/other/OrderDetails"));
const NotFound = lazy(() => import("./pages/other/NotFound"));
const Cookie = lazy(() => import("./pages/other/Cookie"));
const Terms = lazy(() => import("./pages/other/Terms"));
const Privacy = lazy(() => import("./pages/other/Privacy"));

const AppContent = () => {
  const dispatch = useDispatch();
  // const { user } = useSelector((state) => state.user);

  useEffect(() => {
    dispatch(loadUserFromStorage());
    // Fetch general settings
    dispatch(fetchGeneralSettings());
    // Fetch cart and wishlist (handles both Guest/Local and Logged-in/API modes internally)
    dispatch(fetchCart());
    dispatch(fetchWishlist());
  }, [dispatch]);

  return (
    <ScrollToTop>
      <Suspense
        fallback={
          <div className="artote-preloader-wrapper">
            <img
              className="preloader-logo"
              src={process.env.PUBLIC_URL + "/assets/img/logo/logo.png"}
              alt="Artote"
            />
            <div className="artote-preloader">
              <span></span>
              <span></span>
            </div>
          </div>
        }
      >
        <Routes>
          <Route
            path={process.env.PUBLIC_URL + "/"}
            element={<HomeFashionThree />}
          />

          {/* Shop pages - Public */}
          <Route
            path={process.env.PUBLIC_URL + "/shop"}
            element={<ShopGridNoSidebar />}
          />

          {/* Shop product pages */}
          <Route
            path={process.env.PUBLIC_URL + "/product/:slug"}
            element={<Product />}
          />

          {/* Blog pages - Public */}
          <Route
            path={process.env.PUBLIC_URL + "/blog"}
            element={<BlogNoSidebar />}
          />

          <Route
            path={process.env.PUBLIC_URL + "/blog/:slug"}
            element={<BlogDetailsStandard />}
          />

          {/* Other pages - Public */}
          <Route
            path={process.env.PUBLIC_URL + "/about"}
            element={<About />}
          />
          <Route
            path={process.env.PUBLIC_URL + "/contact"}
            element={<Contact />}
          />

          {/* Login page - Public */}
          <Route
            path={process.env.PUBLIC_URL + "/login-register"}
            element={<LoginRegister />}
          />

          {/* Protected Routes - Require Authentication */}
          <Route
            path={process.env.PUBLIC_URL + "/my-account"}
            element={
              <ProtectedRoute>
                <MyAccount />
              </ProtectedRoute>
            }
          />
          <Route
            path={process.env.PUBLIC_URL + "/cart"}
            element={<Cart />}
          />
          <Route
            path={process.env.PUBLIC_URL + "/wishlist"}
            element={<Wishlist />}
          />
          <Route
            path={process.env.PUBLIC_URL + "/compare"}
            element={<Compare />}
          />
          <Route
            path={process.env.PUBLIC_URL + "/checkout"}
            element={<Checkout />}
          />
          <Route
            path={process.env.PUBLIC_URL + "/checkout-success/:id"}
            element={<Success />}
          />
          <Route
            path={process.env.PUBLIC_URL + "/checkout-failed/:id"}
            element={<PaymentFailed />}
          />
          <Route
            path={process.env.PUBLIC_URL + "/my-orders"}
            element={
              <ProtectedRoute>
                <OrderHistory />
              </ProtectedRoute>
            }
          />
          <Route
            path={process.env.PUBLIC_URL + "/order-details/:id"}
            element={
              <ProtectedRoute>
                <OrderDetails />
              </ProtectedRoute>
            }
          />
          <Route path={process.env.PUBLIC_URL + "/terms"} element={<Terms />} />
          <Route path={process.env.PUBLIC_URL + "/privacy"} element={<Privacy />} />
          <Route path={process.env.PUBLIC_URL + "/cookies"} element={<Cookie />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <ToastContainer
        position="top-right"
        autoClose={1000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <CartDrawer />
    </ScrollToTop>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <Router>
        <CartDrawerProvider>
          <AppContent />
        </CartDrawerProvider>
      </Router>
    </AuthProvider>
  );
};

export default App;
